import type { Folder } from '@mystiq/contracts';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Fragment, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ApiError } from '../api/client.ts';
import { keys, useFolders, useWorkspaceId, useWorkspaceInfo, useWorkspaceMutation } from '../api/hooks.ts';
import { useApi } from '../auth/session.tsx';
import { folderPaths } from '../components/MoveDialog.tsx';
import { CommandBar } from '../components/CommandBar.tsx';
import { TransformDialog } from '../components/TransformDialog.tsx';
import { DropZone, UploadButton, UploadStatus, useUploader } from '../components/Upload.tsx';
import { EmptyState, ErrorLine, FolderRow, ItemRow, Modal } from '../components/ui.tsx';
import { Chevron, Edit, Folder as FolderIcon, ICON, Move, NewFolder, Plus, Trash, Write } from '../icons.ts';

export function FilesPage() {
  const ws = useWorkspaceId();
  const { folderId } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const folders = useFolders(ws);
  const info = useWorkspaceInfo(ws);
  const canWrite = info.data ? info.data.role !== 'viewer' : false;
  const all = folders.data ?? [];
  const current = folderId ? all.find((f) => f.id === folderId) : undefined;
  const isInbox = current?.systemRole === 'inbox';
  const subfolders = all.filter((f) => (folderId ? f.parentId === folderId : f.parentId === null));
  const uploader = useUploader(ws, folderId);
  const [dialog, setDialog] = useState<'new-folder' | 'rename' | 'move' | 'delete' | 'write' | null>(null);
  // Items chosen for an action on several at once (Write with AI). Cleared when the folder changes.
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [writing, setWriting] = useState(false);
  useEffect(() => {
    setSelected(new Set());
    setWriting(false);
  }, [folderId]);
  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  const items = useInfiniteQuery({
    queryKey: keys.items(ws, { folderId, group: true }),
    queryFn: ({ pageParam }) => api.items(ws, { ...(folderId ? { folderId } : {}), group: true, ...(pageParam ? { cursor: pageParam } : {}) }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: Boolean(folderId),
  });
  const rows = items.data?.pages.flatMap((page) => page.items) ?? [];

  const createNote = useWorkspaceMutation(ws, () => api.createNote(ws, folderId ? { folderId } : {}));

  if (folderId && folders.isSuccess && !current) {
    return <EmptyState icon={FolderIcon} title="This folder does not exist or was deleted." />;
  }

  const ancestors: Folder[] = [];
  const visited = new Set(current ? [current.id] : []);
  for (let parent = current?.parentId ? all.find((f) => f.id === current.parentId) : undefined; parent && !visited.has(parent.id); ) {
    visited.add(parent.id); // bounded even if the data ever contained a cycle
    ancestors.unshift(parent);
    const next = parent.parentId;
    parent = next ? all.find((f) => f.id === next) : undefined;
  }

  return (
    <DropZone onFiles={(files) => canWrite && folderId && void uploader.upload(files)}>
      <div className="page">
        {current && (
          <nav className="breadcrumbs" aria-label="Folder path">
            <Link to={`/w/${ws}/files`}>Files</Link>
            {ancestors.map((folder) => (
              <Fragment key={folder.id}>
                <Chevron size={ICON.inline} stroke={ICON.stroke} />
                <Link to={`/w/${ws}/folders/${folder.id}`}>{folder.name}</Link>
              </Fragment>
            ))}
          </nav>
        )}
        <h1 className="page-title">{current ? current.name : 'Files'}</h1>
        {isInbox && (
          <p className="meta" style={{ marginTop: 'var(--space-2)' }}>
            Notes and files added without a folder land here. Once AI has read one, it may move it into a fitting folder. Anything you put somewhere yourself stays there.
          </p>
        )}
        {canWrite && (
          <div className="toolbar">
            <button
              className="button button-primary"
              disabled={createNote.isPending}
              onClick={async () => {
                const note = await createNote.mutateAsync(undefined).catch(() => null);
                if (note) navigate(`/w/${ws}/items/${note.id}`);
              }}
            >
              <Plus size={ICON.nav} stroke={ICON.stroke} />
              New note
            </button>
            {folderId && <UploadButton onFiles={(files) => void uploader.upload(files)} />}
            {!isInbox && (
              <button className="button" onClick={() => setDialog('new-folder')}>
                <NewFolder size={ICON.nav} stroke={ICON.stroke} />
                New folder
              </button>
            )}
            {current && !isInbox && (
              <>
                <button className="button" onClick={() => setDialog('rename')}>
                  <Edit size={ICON.nav} stroke={ICON.stroke} />
                  Rename
                </button>
                <button className="button" onClick={() => setDialog('move')}>
                  <Move size={ICON.nav} stroke={ICON.stroke} />
                  Move
                </button>
                <button className="button" onClick={() => setDialog('delete')}>
                  <Trash size={ICON.nav} stroke={ICON.stroke} />
                  Delete folder
                </button>
              </>
            )}
          </div>
        )}
        <div className="content stack">
          <UploadStatus active={uploader.active} errors={uploader.errors} onDismiss={uploader.clearErrors} />
          <ErrorLine error={createNote.error} />
          {selected.size > 0 && (
            <div className="toolbar" role="region" aria-label="Selected items">
              <span className="meta">
                <span className="state">{selected.size} selected.</span>
              </span>
              <button className="button" onClick={() => setDialog('write')}>
                <Write size={ICON.nav} stroke={ICON.stroke} />
                Write with AI
              </button>
              <button className="link-button" onClick={() => setSelected(new Set())}>
                Clear selection
              </button>
            </div>
          )}
          {selected.size > 0 && (
            <CommandBar
              ws={ws}
              placeholder="Ask Mystiq about the selected items, e.g. move these to Research, get rid of these"
              context={() => ({ itemIds: rows.filter((item) => selected.has(item.id)).map((item) => item.id), ...(folderId ? { folderId } : {}) })}
            />
          )}
          {writing && (
            <p className="meta" role="status">
              <span className="state">Writing.</span> The new note appears in this folder, or in Inbox if the items are spread across folders.
            </p>
          )}
          <ul className="rows" aria-label="Folder contents">
            {subfolders.map((folder) => (
              <FolderRow key={folder.id} ws={ws} folder={folder} meta={folder.systemRole === 'inbox' ? 'New notes and uploads land here' : 'Folder'} />
            ))}
            {rows.map((item) => (
              <ItemRow key={item.id} ws={ws} item={item} selection={canWrite ? { selected: selected.has(item.id), onToggle: () => toggle(item.id) } : undefined} />
            ))}
          </ul>
          {items.hasNextPage && (
            <div>
              <button className="button" onClick={() => void items.fetchNextPage()} disabled={items.isFetchingNextPage}>
                Show more
              </button>
            </div>
          )}
          {folderId && items.isSuccess && !rows.length && !subfolders.length && (
            <EmptyState icon={FolderIcon} title="Nothing here yet">
              {canWrite ? 'Create a note, or drop files anywhere on this page to upload them here.' : 'Items added to this folder will appear here.'}
            </EmptyState>
          )}
        </div>
      </div>
      {dialog === 'write' && (
        <TransformDialog
          ws={ws}
          sourceItemIds={rows.filter((item) => selected.has(item.id)).map((item) => item.id)}
          onClose={() => setDialog(null)}
          onRequested={() => {
            setSelected(new Set());
            setWriting(true);
          }}
        />
      )}
      {dialog === 'new-folder' && <NameDialog title="New folder" action="Create folder" initial="" onClose={() => setDialog(null)} onSave={(name) => api.createFolder(ws, name, folderId ?? null)} ws={ws} />}
      {dialog === 'rename' && current && (
        <NameDialog title="Rename folder" action="Rename" initial={current.name} onClose={() => setDialog(null)} onSave={(name) => api.renameFolder(ws, current.id, name)} ws={ws} />
      )}
      {dialog === 'move' && current && <MoveFolderDialog ws={ws} folder={current} folders={all} onClose={() => setDialog(null)} />}
      {dialog === 'delete' && current && (
        <DeleteFolderDialog ws={ws} folder={current} onClose={() => setDialog(null)} onDeleted={() => navigate(current.parentId ? `/w/${ws}/folders/${current.parentId}` : `/w/${ws}/files`)} />
      )}
    </DropZone>
  );
}

function NameDialog({ ws, title, action, initial, onSave, onClose }: { ws: string; title: string; action: string; initial: string; onSave(name: string): Promise<unknown>; onClose(): void }) {
  const [name, setName] = useState(initial);
  const save = useWorkspaceMutation(ws, (value: string) => onSave(value));
  return (
    <Modal title={title} onClose={onClose}>
      <form
        className="stack"
        onSubmit={async (event) => {
          event.preventDefault();
          if (!name.trim()) return;
          // Errors stay on screen via save.error.
          if (await save.mutateAsync(name.trim()).then(() => true, () => false)) onClose();
        }}
      >
        <label className="field">
          <span className="field-label">Name</span>
          <input className="input" value={name} onChange={(event) => setName(event.target.value)} maxLength={200} />
        </label>
        <ErrorLine error={save.error} />
        <div className="modal-actions">
          <button className="button button-primary" type="submit" disabled={!name.trim() || save.isPending}>
            {action}
          </button>
          <button className="button" type="button" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
}

function MoveFolderDialog({ ws, folder, folders, onClose }: { ws: string; folder: Folder; folders: Folder[]; onClose(): void }) {
  const api = useApi();
  const [parent, setParent] = useState(folder.parentId ?? '');
  const move = useWorkspaceMutation(ws, (parentId: string | null) => api.moveFolder(ws, folder.id, parentId));
  const options = folderPaths(folders).filter((option) => !option.isInbox && option.id !== folder.id);
  return (
    <Modal title="Move folder" onClose={onClose}>
      <label className="field">
        <span className="field-label">Put it inside</span>
        <select className="select" value={parent} onChange={(event) => setParent(event.target.value)}>
          <option value="">Top level</option>
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.path}
            </option>
          ))}
        </select>
      </label>
      <ErrorLine error={move.error} />
      <div className="modal-actions">
        <button
          className="button button-primary"
          disabled={move.isPending}
          onClick={async () => {
            if (await move.mutateAsync(parent || null).then(() => true, () => false)) onClose();
          }}
        >
          Move here
        </button>
        <button className="button" onClick={onClose}>
          Cancel
        </button>
      </div>
    </Modal>
  );
}

function DeleteFolderDialog({ ws, folder, onClose, onDeleted }: { ws: string; folder: Folder; onClose(): void; onDeleted(): void }) {
  const api = useApi();
  const remove = useWorkspaceMutation(ws, () => api.deleteFolder(ws, folder.id));
  const notEmpty = remove.error instanceof ApiError && remove.error.code === 'FOLDER_NOT_EMPTY';
  return (
    <Modal title={`Delete "${folder.name}"?`} onClose={onClose}>
      <p>Only empty folders can be deleted. Items in Trash that lived here will restore to Inbox.</p>
      {notEmpty ? (
        <p role="alert">
          <span className="state">Not deleted.</span> This folder still has items or folders in it. Move or trash them first.
        </p>
      ) : (
        <ErrorLine error={remove.error} />
      )}
      <div className="modal-actions">
        <button
          className="button button-primary"
          disabled={remove.isPending}
          onClick={async () => {
            if (await remove.mutateAsync(undefined).then(() => true, () => false)) {
              onClose();
              onDeleted();
            }
          }}
        >
          Delete folder
        </button>
        <button className="button" onClick={onClose}>
          Cancel
        </button>
      </div>
    </Modal>
  );
}
